<script setup lang="ts">
import { professionalTrackOptions, specialityOptions } from "assets/data/about-you-options";
import type { Course } from "~/composables/useCourse";
import { KickoffMasteryTrackingEvents } from "~/enums/tracking/kickoff-mastery.enums";

const { user } = useUserStore();
const { setCourseActivityInformationAccepted } = useCourseState();
const { fetchUserInfo, userInfo } = useAccount();
const { dispatchTrackingEvent } = useTracking();

const props = defineProps<{
  course: Course;
  activityInformationAccepted?: boolean;
}>();

const modelValue = defineModel<boolean>();
const cmeDisclaimerText = ref('');
const isLoading = ref(false);
const isMounted = ref(false);

const fetchCmeDisclaimer = async () => {
  return await useErrorHandling(async () => {
    if (!props.course.id) {
      throw new Error('Course ID is required to get course.');
    }

    isLoading.value = true;
    const url = useApiUrl(`/courses/${props.course.id}/activity-information`);
    const response = await useAuthenticatedFetch(url);
    cmeDisclaimerText.value = typeof response.data.value === 'string' ? response.data.value : '';

    isLoading.value = false;
  });
};

const enrollmentError = ref();

const accept = async () => {
  isLoading.value = true;
  enrollmentError.value = null;

  if (!props.course.id) {
    throw new Error('Course ID is required to enroll.');
  }

  try {
    const url = useApiUrl(`/courses/${props.course.id}/enroll`);
    await useAuthenticatedFetch(url, {
      method: 'POST',
      body: {
        activityInformationAccepted: true,
        professionalTrack: userInfo.value?.professionalTrack,
        professionalTrackOther: userInfo.value?.professionalTrackOther,
        specialty: userInfo.value?.specialty,
        specialtyOther: userInfo.value?.specialtyOther,
      },
    });

    dispatchTrackingEvent(KickoffMasteryTrackingEvents.ActivityAccepted, {
      'user': {
        'plan_type': user.value?.subscription?.type,
      },
      'user_specialty': userInfo.value?.specialty,
      'course_id': props.course.id,
    });

    setCourseActivityInformationAccepted(props.course.id);

    dismiss(true);
  } catch (error) {
    enrollmentError.value = error;
  } finally {
    isLoading.value = false;
  }
};

const isEnrollmentFormValid = computed(() => {
  if (!props.course.kickoffAndMasteryQuizzesEnabled) {
    return true;
  }

  if (!userInfo.value?.professionalTrack?.length || !userInfo.value?.specialty?.length) {
    return false;
  }

  // fixme: use variable for 'other'
  if (userInfo.value?.professionalTrack === 'other' && (userInfo.value?.professionalTrackOther?.length || 0) < 3) {
    return false;
  }

  // fixme: use variable for 'other'
  if (userInfo.value?.specialty === 'other' && (userInfo.value?.specialtyOther?.length || 0) < 3) {
    return false;
  }

  return true;
});

const dismiss = (accepted = false) => {
  modelValue.value = false;

  if (!accepted) {
    navigateTo('/courses/' + props.course.id);
  }
};

onBeforeMount(async () => {
  if (props.course.cmeCredits === 0) {
    console.warn('This course has no CME Credits, therefore no activity information is required.');
    return;
  }

  await fetchCmeDisclaimer();
  await fetchUserInfo();
  isMounted.value = true;
});

watch(
  () => props.course.id,
  async newCourseId => {
    if (newCourseId) {
      await fetchCmeDisclaimer();
    }
  },
);

</script>

<template>
  <Dialog
    v-if="isMounted"
    v-model:visible="modelValue"
    :draggable="false"
    modal
    header="Activity Information"
    class="sm:w-[586px]"
    :closable="false"
    :pt="{
      header: {
        class: 'justify-center'
      },
      content: {
        class: 'p-6 rounded-none'
      }
    }"
  >
    <template #default>
      <template v-if="userInfo && course.kickoffAndMasteryQuizzesEnabled">
        <div class="uppercase text-16 font-bold text-neutral-60 mb-6">
          Profile information update
        </div>

        <div class="text-12 font-bold mb-2">
          What is your profession *
        </div>
        <Dropdown
          v-model="userInfo.professionalTrack"
          placeholder="Please Select"
          option-value="value"
          option-label="label"
          scroll-height="250px"
          filter-placeholder="Please choose one option"
          filter
          :options="professionalTrackOptions"
          append-to="self"
          class="w-full"
        />
        <Textarea
          v-if="userInfo.professionalTrack === 'other'"
          v-model="userInfo.professionalTrackOther"
          placeholder="Please specify"
          max-length="50"
          rows="3"
          class="w-full mt-2 resize-y"
        />

        <div class="text-12 font-bold mb-2 mt-4">
          What is your specialty *
        </div>
        <Dropdown
          v-model="userInfo.specialty"
          placeholder="Please Select"
          option-value="value"
          option-label="label"
          scroll-height="250px"
          filter
          :options="specialityOptions"
          append-to="self"
          class="w-full"
        />
        <Textarea
          v-if="userInfo.specialty === 'other'"
          v-model="userInfo.specialtyOther"
          placeholder="Please specify"
          max-length="50"
          rows="3"
          class="w-full mt-2 resize-y"
        />

        <Divider class="my-8" />
      </template>

      <div class="uppercase text-16 font-bold text-neutral-60 mb-6">
        Read and accept this activity information
      </div>

      <div class="activity-information-modal">
        <div class="activity-information">
          <!--eslint-disable-next-line vue/no-v-html-->
          <div v-html="cmeDisclaimerText" />
        </div>
      </div>
    </template>

    <template #footer>
      <Button
        severity="secondary"
        :loading="isLoading"
        class="rounded-full text-neutral-80 border-neutral-90 font-bold"
        outlined
        @click="() => dismiss()"
      >
        Close
      </Button>

      <Button
        v-if="!activityInformationAccepted"
        severity="info"
        :loading="isLoading"
        :disabled="!isEnrollmentFormValid"
        class="rounded-full text-nowrap bg-blue-30 border-blue-30 text-neutral-90 font-bold"
        @click="accept"
      >
        Accept & Continue
      </Button>
    </template>
  </Dialog>
</template>

<style lang="scss">
@import "@mui/assets/styles/utility.scss";
@import "@mui/assets/styles/font-styles.scss";

.activity-information-modal {
  display: grid;
  grid-template-rows: 1fr auto;
  height: 100%;

  .activity-information {
    .activity-information-intro {
      @apply bg-neutral-10 rounded-lg p-4 mb-4;

      p {
        @apply text-16 text-neutral-80 m-0 p-0;
      }
    }

    h2, h4 {
      @apply text-18 font-bold mb-2 mt-6 text-neutral-100;
    }

    p {
      @apply text-16 m-0 p-0 text-neutral-80;
    }

    ol {
      @apply pl-8;
      li {
        @apply text-left;
      }
    }

    .logo {
      @apply w-[200px] mb-2;
    }
  }
}
</style>
